// frontend/modules/document-type/stores/document-type-store.ts
import { create } from 'zustand';
import { DocumentTypeFiltersData, DocumentTypeStats } from '../types/document-type.types';

interface DocumentTypeStore {
    // Filters state
    filters: DocumentTypeFiltersData;
    setFilters: (filters: Partial<DocumentTypeFiltersData>) => void;
    clearFilters: () => void;
    
    // Selection state
    selectedDocumentTypes: string[];
    setSelectedDocumentTypes: (codes: string[]) => void;
    toggleDocumentTypeSelection: (code: string) => void;
    clearSelection: () => void;
    
    // UI state
    isFormModalOpen: boolean;
    setFormModalOpen: (open: boolean) => void;
    
    isDeleteModalOpen: boolean;
    setDeleteModalOpen: (open: boolean) => void;
    
    editingDocumentType: string | null;
    setEditingDocumentType: (code: string | null) => void;
    
    // Stats
    stats: DocumentTypeStats | null;
    setStats: (stats: DocumentTypeStats) => void;
    
    // Loading states
    isLoading: boolean;
    setLoading: (loading: boolean) => void;
    
    // Error state
    error: string | null;
    setError: (error: string | null) => void;
    
    // Actions
    reset: () => void;
}

const initialFilters: DocumentTypeFiltersData = {
    search: '',
    activeOnly: undefined,
    requiredOnly: undefined
};

const initialStats: DocumentTypeStats = {
    total: 0,
    active: 0,
    required: 0,
    withSizeConstraints: 0
};

export const useDocumentTypeStore = create<DocumentTypeStore>((set, get) => ({
    // Filters state
    filters: initialFilters,
    setFilters: (newFilters) => set((state) => ({
        filters: { ...state.filters, ...newFilters }
    })),
    clearFilters: () => set({ filters: initialFilters }),
    
    // Selection state
    selectedDocumentTypes: [],
    setSelectedDocumentTypes: (codes) => set({ selectedDocumentTypes: codes }),
    toggleDocumentTypeSelection: (code) => set((state) => ({
        selectedDocumentTypes: state.selectedDocumentTypes.includes(code)
            ? state.selectedDocumentTypes.filter(c => c !== code)
            : [...state.selectedDocumentTypes, code]
    })),
    clearSelection: () => set({ selectedDocumentTypes: [] }),
    
    // UI state
    isFormModalOpen: false,
    setFormModalOpen: (open) => set({ isFormModalOpen: open }),
    
    isDeleteModalOpen: false,
    setDeleteModalOpen: (open) => set({ isDeleteModalOpen: open }),
    
    editingDocumentType: null,
    setEditingDocumentType: (code) => set({ editingDocumentType: code }),
    
    // Stats
    stats: initialStats,
    setStats: (stats) => set({ stats }),
    
    // Loading states
    isLoading: false,
    setLoading: (loading) => set({ isLoading: loading }),
    
    // Error state
    error: null,
    setError: (error) => set({ error }),
    
    // Actions
    reset: () => set({
        filters: initialFilters,
        selectedDocumentTypes: [],
        isFormModalOpen: false,
        isDeleteModalOpen: false,
        editingDocumentType: null,
        stats: initialStats,
        isLoading: false,
        error: null
    })
}));
